/**
 * Editor de modelo de checklist (criar ou editar).
 *
 * Salvar publica pela rota de sempre: sem mudança → "sem alterações"; com
 * mudança → versão nova, e os checklists já feitos seguem na versão deles.
 * Códigos de seção e item são preservados (o histórico junta por eles); item
 * novo ganha código a partir do nome.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  MaintenanceAnswerTypeDto, MaintenanceChecklistKindDto, MaintenanceCriticalityDto, MaintenanceDefectFamilyDto,
  MaintenancePublishResultDto, MaintenanceTemplateDefDto, MaintenanceTemplateDefinitionDto, MaintenanceTemplateItemDefDto,
  MaintenanceTemplateSummaryDto, MaintenanceVehicleTypeDto,
} from "@bi/types";
import { api, ApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { useCapabilities } from "../features/maintenance/useCapabilities";
import { ExecutionEditor } from "../features/maintenance/ExecutionEditor";
import { KIND_LABEL } from "../features/maintenance/labels";
import {
  ANSWER_TYPE_LABEL, OUTRO, TRIGGERS_BY_TYPE, addDefect, applyDefectFamily, editorProblems, move, newItem, newSection,
  slugCode, toPublishPayload, uniqueCode, withType,
} from "../features/maintenance/template-editor";

const TIPOS_NOVOS: MaintenanceChecklistKindDto[] = ["inspection", "daily", "maintenance", "work_order"];
const CRIT: Array<{ value: MaintenanceCriticalityDto; label: string }> = [
  { value: "low", label: "Baixa" }, { value: "medium", label: "Média" }, { value: "high", label: "Alta" },
];

const vazio = (): MaintenanceTemplateDefDto => ({
  code: "", name: "", description: "", kind: "inspection", implementType: null,
  sections: [{ code: "geral", title: "Geral", items: [] }],
});

export function MaintenanceTemplateEditorPage() {
  const { id = "novo" } = useParams();
  const novo = id === "novo";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useCapabilities();

  const carregado = useQuery({
    queryKey: ["maintenance", "template-definition", id],
    queryFn: () => api.get<MaintenanceTemplateDefinitionDto>(`/maintenance/templates/${id}/definition`),
    enabled: !novo,
  });
  const todos = useQuery({
    queryKey: ["maintenance", "templates", "all"],
    queryFn: () => api.get<{ items: MaintenanceTemplateSummaryDto[] }>("/maintenance/templates?all=1"),
  });
  const tipos = useQuery({
    queryKey: ["maintenance", "vehicle-types"],
    queryFn: () => api.get<{ items: MaintenanceVehicleTypeDto[] }>("/maintenance/vehicle-types"),
  });
  const familias = useQuery({
    queryKey: ["maintenance", "defect-families"],
    queryFn: () => api.get<{ items: MaintenanceDefectFamilyDto[] }>("/maintenance/defect-families"),
    staleTime: Infinity,
  });

  const [def, setDef] = useState<MaintenanceTemplateDefDto | null>(novo ? vazio() : null);
  const [tipoVeiculo, setTipoVeiculo] = useState<string>("");
  const [resultado, setResultado] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (carregado.data) {
      setDef(carregado.data.definition);
      setTipoVeiculo(carregado.data.template.vehicleTypeId ?? "");
    }
  }, [carregado.data]);

  const problemas = useMemo(() => (def ? editorProblems(def) : []), [def]);
  const fixo = !novo && (def?.kind === "delivery" || def?.kind === "return_delivery");

  const salvar = useMutation({
    mutationFn: () => {
      const payload = toPublishPayload(def!);
      if (novo) payload.code = uniqueCode(slugCode(payload.name), (todos.data?.items ?? []).map((t) => t.code));
      return api.post<MaintenancePublishResultDto>("/maintenance/templates", { ...payload, vehicleTypeId: tipoVeiculo || null });
    },
    onSuccess: (r) => {
      setErro(null);
      qc.invalidateQueries({ queryKey: ["maintenance"] });
      setResultado(
        r.outcome === "unchanged" ? "Nada mudou no conteúdo, nenhuma versão nova."
          : r.outcome === "created" ? `Modelo criado (versão ${r.version}, ${r.items} itens).`
            : `Versão ${r.version} publicada (${r.items} itens). Checklists já feitos continuam na versão anterior.`,
      );
      if (novo) navigate(`/frota/manutencao/modelos/${r.templateId}`, { replace: true });
    },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível salvar."),
  });

  if (!can("template.manage")) {
    return <div className="card p-6 text-sm text-gray-600">Seu perfil não edita modelos de checklist. <Link className="text-tm-600" to="/frota/manutencao/modelos">Voltar</Link></div>;
  }
  if (!def) return <div className="p-8 text-sm text-gray-500">Carregando modelo…</div>;

  const set = (patch: Partial<MaintenanceTemplateDefDto>) => { setResultado(null); setDef({ ...def, ...patch }); };
  const setSecao = (si: number, patch: Partial<MaintenanceTemplateDefDto["sections"][number]>) =>
    set({ sections: def.sections.map((s, i) => (i === si ? { ...s, ...patch } : s)) });
  const setItem = (si: number, ii: number, item: MaintenanceTemplateItemDefDto) =>
    setSecao(si, { items: def.sections[si]!.items.map((x, j) => (j === ii ? item : x)) });

  return (
    <div className="space-y-5 pb-24">
      <PageHeader
        title={novo ? "Novo modelo de checklist" : def.name || "Modelo"}
        subtitle={novo ? "Monte seções e itens. Ao salvar o modelo já aparece para iniciar no app." : `Versão publicada: v${carregado.data?.version.version} · ${carregado.data?.runCount ?? 0} checklist(s) feitos com este modelo`}
        actions={<Link className="btn-secondary [[data-layout=mobile]_&]:hidden" to="/frota/manutencao/modelos">Voltar</Link>}
      />

      <div className="card p-5 grid sm:grid-cols-2 gap-4">
        <label className="block sm:col-span-2">
          <span className="text-sm text-gray-600">Nome</span>
          <input className="input w-full mt-1" value={def.name} placeholder="Ex.: Empilhadeira, antes do turno" onChange={(e) => set({ name: e.target.value })} />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-sm text-gray-600">Descrição</span>
          <input className="input w-full mt-1" value={def.description} onChange={(e) => set({ description: e.target.value })} />
        </label>
        <label className="block">
          <span className="text-sm text-gray-600">Uso</span>
          <select className="input w-full mt-1" value={def.kind} disabled={!novo} onChange={(e) => set({ kind: e.target.value as MaintenanceChecklistKindDto })}>
            {(novo ? TIPOS_NOVOS : [def.kind]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
          </select>
          {!novo && <span className="text-xs text-gray-400">O uso não muda depois de criado, porque mudaria o sentido dos checklists antigos.</span>}
        </label>
        <label className="block">
          <span className="text-sm text-gray-600">Tipo de veículo / operação</span>
          <select className="input w-full mt-1" value={tipoVeiculo} disabled={fixo} onChange={(e) => { setResultado(null); setTipoVeiculo(e.target.value); }}>
            <option value="">{fixo ? "Cavalo + carreta (frota)" : "Qualquer veículo"}</option>
            {(tipos.data?.items ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <Link className="text-xs text-tm-600" to="/frota/manutencao/modelos">Cadastrar tipos</Link>
        </label>
      </div>

      {def.sections.map((s, si) => (
        <section key={s.code} className="card p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <input className="input flex-1 min-w-[12.5rem] font-medium" value={s.title} placeholder="Título da seção" onChange={(e) => setSecao(si, { title: e.target.value })} />
            <button className="btn-ghost text-xs" disabled={si === 0} onClick={() => set({ sections: move(def.sections, si, -1) })}>↑</button>
            <button className="btn-ghost text-xs" disabled={si === def.sections.length - 1} onClick={() => set({ sections: move(def.sections, si, 1) })}>↓</button>
            <button className="btn-ghost text-xs text-danger-600" onClick={() => set({ sections: def.sections.filter((_, i) => i !== si) })}>Remover seção</button>
          </div>
          <input className="input w-full text-sm" value={s.helpText ?? ""} placeholder="Instrução da seção (opcional)" onChange={(e) => setSecao(si, { helpText: e.target.value })} />

          <div className="divide-y divide-gray-100 border border-surface-border rounded-lg">
            {s.items.map((it, ii) => (
              <ItemEditor
                key={it.code}
                item={it}
                familias={familias.data?.items ?? []}
                execucao={def.kind === "work_order"}
                primeiro={ii === 0}
                ultimo={ii === s.items.length - 1}
                onChange={(novoItem) => setItem(si, ii, novoItem)}
                onMove={(d) => setSecao(si, { items: move(s.items, ii, d) })}
                onRemove={() => setSecao(si, { items: s.items.filter((_, j) => j !== ii) })}
              />
            ))}
            {s.items.length === 0 && <p className="p-3 text-sm text-gray-400">Sem itens nesta seção.</p>}
          </div>
          <button
            className="btn-secondary text-sm"
            onClick={() => setSecao(si, { items: [...s.items, newItem("", "conformity", def.sections.flatMap((x) => x.items.map((i) => i.code)))] })}
          >
            + Item
          </button>
        </section>
      ))}

      <button className="btn-secondary" onClick={() => set({ sections: [...def.sections, newSection("", def.sections.map((x) => x.code))] })}>+ Seção</button>

      {/* Barra de salvar */}
      <div className="fixed bottom-0 left-0 lg:left-64 right-0 bg-white border-t border-surface-border p-3 z-30 [[data-layout=mobile]_&]:bottom-[calc(3.625rem+env(safe-area-inset-bottom))] lg:[[data-layout=mobile]_&]:bottom-0 lg:[[data-layout=mobile]_&]:left-0">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-0 text-sm">
            {erro ? <span className="text-danger-600">{erro}</span>
              : resultado ? <span className="text-tm-700">{resultado}</span>
                : problemas.length ? <span className="text-warning-700">{problemas[0]}{problemas.length > 1 ? ` (+${problemas.length - 1})` : ""}</span>
                  : <span className="text-gray-500">{def.sections.reduce((a, s) => a + s.items.length, 0)} itens em {def.sections.length} seção(ões)</span>}
          </div>
          <button className="btn-primary" disabled={problemas.length > 0 || salvar.isPending} onClick={() => salvar.mutate()}>
            {salvar.isPending ? "Publicando…" : novo ? "Criar modelo" : "Publicar alterações"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ItemEditor({ item, familias, execucao, primeiro, ultimo, onChange, onMove, onRemove }: {
  item: MaintenanceTemplateItemDefDto;
  /** modelo "Execução da O.S.": o item pode ter execução técnica */
  execucao: boolean;
  familias: MaintenanceDefectFamilyDto[];
  primeiro: boolean;
  ultimo: boolean;
  onChange: (i: MaintenanceTemplateItemDefDto) => void;
  onMove: (d: -1 | 1) => void;
  onRemove: () => void;
}) {
  const [aberto, setAberto] = useState(item.label === "");
  const [novoProblema, setNovoProblema] = useState("");
  const [novaOpcao, setNovaOpcao] = useState("");
  const gatilhos = TRIGGERS_BY_TYPE[item.answerType];

  return (
    <div className="p-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <input className="input flex-1 min-w-[11.25rem]" value={item.label} placeholder="Nome do item (ex.: Farol)" onChange={(e) => onChange({ ...item, label: e.target.value })} />
        <select className="input w-56" value={item.answerType} onChange={(e) => onChange(withType(item, e.target.value as MaintenanceAnswerTypeDto))}>
          {(Object.keys(ANSWER_TYPE_LABEL) as MaintenanceAnswerTypeDto[]).map((t) => <option key={t} value={t}>{ANSWER_TYPE_LABEL[t]}</option>)}
        </select>
        <label className="inline-flex items-center gap-1 text-xs text-gray-600">
          <input type="checkbox" checked={item.required ?? false} onChange={(e) => onChange({ ...item, required: e.target.checked })} /> Obrigatório
        </label>
        <button className="btn-ghost text-xs" onClick={() => setAberto(!aberto)}>{aberto ? "Menos" : "Mais"}</button>
        <button className="btn-ghost text-xs" disabled={primeiro} onClick={() => onMove(-1)}>↑</button>
        <button className="btn-ghost text-xs" disabled={ultimo} onClick={() => onMove(1)}>↓</button>
        <button className="btn-ghost text-xs text-danger-600" onClick={onRemove} aria-label="Remover item">×</button>
      </div>

      {aberto && (
        <div className="pl-1 grid sm:grid-cols-3 gap-3 text-sm">
          <label className="block">
            <span className="text-xs text-gray-500">Criticidade</span>
            <select className="input w-full" value={item.criticality ?? "medium"} onChange={(e) => onChange({ ...item, criticality: e.target.value as MaintenanceCriticalityDto })}>
              {CRIT.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs text-gray-500">Ocorrência</span>
            <select className="input w-full" value={item.nonConformityTrigger ?? gatilhos[0]!.value} onChange={(e) => onChange({ ...item, nonConformityTrigger: e.target.value as typeof gatilhos[number]["value"] })}>
              {gatilhos.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </label>
          <label className="inline-flex items-center gap-2 text-xs text-gray-600 sm:mt-5">
            <input type="checkbox" checked={item.requiresPhotoOnNonConformity ?? false} onChange={(e) => onChange({ ...item, requiresPhotoOnNonConformity: e.target.checked })} />
            Foto obrigatória na ocorrência
          </label>
          {execucao && <ExecutionEditor value={item.execution} onChange={(execution) => onChange({ ...item, execution })} />}
          <label className="block sm:col-span-3">
            <span className="text-xs text-gray-500">Ajuda para quem responde (opcional)</span>
            <input className="input w-full" value={item.helpText ?? ""} onChange={(e) => onChange({ ...item, helpText: e.target.value })} />
          </label>

          {item.answerType === "quantity" && item.nonConformityTrigger === "below_minimum" && (
            <label className="block">
              <span className="text-xs text-gray-500">Quantidade mínima</span>
              <input className="input w-full" inputMode="numeric" value={item.minQuantity ?? ""} onChange={(e) => onChange({ ...item, minQuantity: e.target.value === "" ? undefined : Math.max(0, Number(e.target.value.replace(/\D/g, ""))) })} />
            </label>
          )}

          {item.answerType === "choice" && (
            <div className="sm:col-span-3 space-y-1">
              <span className="text-xs text-gray-500">Opções (marque as que abrem ocorrência)</span>
              {(item.options ?? []).map((o, k) => (
                <div key={o.value} className="flex items-center gap-2">
                  <input className="input flex-1" value={o.label} onChange={(e) => onChange({ ...item, options: item.options!.map((x, j) => (j === k ? { ...x, label: e.target.value } : x)) })} />
                  <label className="inline-flex items-center gap-1 text-xs text-gray-600">
                    <input type="checkbox" checked={!!o.nonConforming} onChange={(e) => onChange({ ...item, options: item.options!.map((x, j) => (j === k ? { ...x, nonConforming: e.target.checked || undefined } : x)) })} /> abre ocorrência
                  </label>
                  <button className="btn-ghost text-xs" onClick={() => onChange({ ...item, options: item.options!.filter((_, j) => j !== k) })}>×</button>
                </div>
              ))}
              <div className="flex gap-2">
                <input className="input flex-1" placeholder="Nova opção" value={novaOpcao} onChange={(e) => setNovaOpcao(e.target.value)} />
                <button className="btn-secondary text-xs" disabled={novaOpcao.trim().length < 1} onClick={() => {
                  const value = uniqueCode(slugCode(novaOpcao).slice(0, 40), (item.options ?? []).map((o) => o.value));
                  onChange({ ...item, options: [...(item.options ?? []), { value, label: novaOpcao.trim() }] });
                  setNovaOpcao("");
                }}>Adicionar</button>
              </div>
            </div>
          )}

          {item.answerType === "conformity" && (
            <div className="sm:col-span-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-gray-500">Problemas que o operador escolhe ao marcar "Não conforme"</span>
                <select className="input w-60 text-xs" value="" onChange={(e) => {
                  const f = familias.find((x) => x.code === e.target.value);
                  if (f) onChange(applyDefectFamily(item, f.options));
                }}>
                  <option value="">Usar uma lista pronta…</option>
                  {familias.map((f) => <option key={f.code} value={f.code}>{f.label}</option>)}
                </select>
              </div>
              <div className="flex flex-wrap gap-2">
                {(item.defectOptions ?? []).map((o, k) => (
                  <span key={o.value} className="inline-flex items-center gap-1 rounded-full border border-surface-border bg-surface-muted px-2.5 py-1 text-xs">
                    {o.label}
                    {o.criticality === "high" && <span className="text-danger-600">· Alta</span>}
                    {o.value !== OUTRO.value && (
                      <button className="text-gray-400 hover:text-danger-600" aria-label={`Remover ${o.label}`} onClick={() => onChange({ ...item, defectOptions: item.defectOptions!.filter((_, j) => j !== k) })}>×</button>
                    )}
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <input className="input flex-1" placeholder="Novo problema (ex.: Lâmpada queimada)" value={novoProblema} onChange={(e) => setNovoProblema(e.target.value)} />
                <button className="btn-secondary text-xs" disabled={novoProblema.trim().length < 2} onClick={() => { onChange(addDefect(item, novoProblema)); setNovoProblema(""); }}>Adicionar</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
